import { InputParameterHierarchyType } from '../model'
import { TemplateInputParameters } from '../../../models/AiFeature'
import React, { useEffect } from 'react'
import { useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useInputParametersUpdateScope } from '../hooks'
import { useUserTemplateConfigForm } from '../../../views/Setup/AIFeaturePresets/PresetConfig/Template/Hierarchy/hooks'
import { YesNoCancelModal } from '../../Modal/YesNoCancelModal'
import { TemplateBox } from '../../TemplateBox/TemplateBox'
import { Button } from '../../Button/Button'
import { ScopedBlock } from '../../ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../ScopedBlock/models'

interface CurrentUserConfigFormBodyProps {
  type: InputParameterHierarchyType
  typeId: string
  inputParametersSchema: { [key: string]: any } | undefined
  defaultInputParameters: TemplateInputParameters
  parentInputParameters: TemplateInputParameters
  inputParameters: TemplateInputParameters
  isCustomized: boolean
  showCustomizationSetting: boolean
  onSave: (inputParameters: { [key: string]: any }, isCustomized: boolean) => Promise<void>
  onHide: () => void
  updateLoading: boolean
  ipHierarchyEnabled: boolean
}

export const CurrentUserConfigFormBody: React.FC<CurrentUserConfigFormBodyProps> = ({
  typeId,
  defaultInputParameters,
  parentInputParameters,
  inputParameters,
  isCustomized,
  showCustomizationSetting,
  onSave,
  onHide,
  updateLoading,
  ipHierarchyEnabled,
}) => {
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const { hasIpHierarchyUpdateScope } = useInputParametersUpdateScope(currentUserId)
  const disabled = !ipHierarchyEnabled || !hasIpHierarchyUpdateScope

  const {
    editedInputParameters,
    templateHasValue,
    isChanged,
    customized,
    showConfirmation,
    shouldReset,
    setEditedInputParameters,
    setShowConfirmation,
    handleChangeCustomized,
    handleChangeTemplate,
    handleClickBack,
    handleClickCancel,
    handleClickSave,
    checkForChange,
  } = useUserTemplateConfigForm(inputParameters, parentInputParameters, isCustomized, onSave, onHide, disabled)

  useEffect(() => {
    checkForChange()
  }, [checkForChange])

  return (
    <>
      <YesNoCancelModal
        show={showConfirmation}
        onHide={() => setShowConfirmation(false)}
        message={'Do you want to save your changes?'}
        onYes={() => {
          onSave(editedInputParameters, customized)
          onHide()
        }}
        onNo={() => {
          setEditedInputParameters(inputParameters)
          onHide()
        }}
      />
      <TemplateBox
        shouldShowGenerate={true}
        onGenerate={() => {}}
        template={inputParameters.template}
        defaultTemplate={defaultInputParameters.template}
        parentTemplate={parentInputParameters.template}
        generatedTemplate={''}
        onChangeTemplate={handleChangeTemplate}
        customized={customized}
        onChangeCustomized={handleChangeCustomized}
        showCustomizationSetting={showCustomizationSetting}
        shouldReset={shouldReset}
        disabled={disabled}
        typeId={typeId}
      />
      <div className='w-100 d-flex justify-content-between zocks-mt-3'>
        <Button variant='borderless' onClick={handleClickBack}>
          Close
        </Button>
        {hasIpHierarchyUpdateScope && (
          <div className='d-flex gap-2'>
            <>
              {ipHierarchyEnabled && (
                <Button variant='secondary' onClick={handleClickCancel} disabled={!isChanged} loading={updateLoading}>
                  Cancel
                </Button>
              )}
              <ScopedBlock
                missingScopeAction={MissingScopeAction.UPGRADE}
                quota={{
                  quota: 'ai.ipHierarchy.value',
                }}
              >
                <Button variant='primary' onClick={handleClickSave} disabled={ipHierarchyEnabled && (!isChanged || !templateHasValue)} loading={updateLoading}>
                  {ipHierarchyEnabled ? 'Save' : 'Get feature'}
                </Button>
              </ScopedBlock>
            </>
          </div>
        )}
      </div>
    </>
  )
}
